import {
  COMPANY_ADDRESS,
  CONTACT_EMAILS,
  CONTACT_PHONE,
  PRODUCT_BRAND,
  SITE_ORIGINS,
  getOgImageUrl,
  getSiteOrigin,
  type SiteLanguage,
} from "@/config/platform";

const EU_DESTINATION_AREA_SERVED = [
  { "@type": "Country", "name": "Portugal" },
  { "@type": "Country", "name": "Spain" },
  { "@type": "Country", "name": "Italy" },
  { "@type": "Country", "name": "Greece" },
];

// Structured data schemas for different page types

export const createOrganizationSchema = (lang: SiteLanguage = "pt") => {
  const url = getSiteOrigin(lang);
  const logoUrl = getOgImageUrl(lang);

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${url}/#organization`,
    "name": PRODUCT_BRAND,
    "description": "International residency intelligence across Portugal, Spain, Italy, and Greece",
    "url": url,
    "sameAs": [SITE_ORIGINS.en, SITE_ORIGINS.pt],
    "logo": {
      "@type": "ImageObject",
      "url": logoUrl,
      "width": 1200,
      "height": 630,
    },
    "image": logoUrl,
    "email": CONTACT_EMAILS.info,
    "telephone": CONTACT_PHONE.e164,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": CONTACT_PHONE.e164,
      "email": CONTACT_EMAILS.info,
      "contactType": "customer service",
      "availableLanguage": ["Portuguese", "English"],
      "areaServed": EU_DESTINATION_AREA_SERVED,
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": COMPANY_ADDRESS.line1,
      "addressLocality": "Santa Maria da Feira",
      "postalCode": "4520-211",
      "addressRegion": "Aveiro",
      "addressCountry": "PT",
    },
  };
};

export const createWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": PRODUCT_BRAND,
  "url": SITE_ORIGINS.pt,
  "description": "International residency platform for Portugal, Spain, Italy, and Greece",
  "publisher": {
    "@type": "Organization",
    "name": PRODUCT_BRAND
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${SITE_ORIGINS.pt}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
});

export const createServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": service.name,
  "description": service.description,
  "url": service.url,
  "provider": {
    "@type": "Organization",
    "name": PRODUCT_BRAND
  },
  "areaServed": EU_DESTINATION_AREA_SERVED
});

export const createArticleSchema = (article: {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.description,
  "url": article.url,
  "datePublished": article.datePublished,
  "dateModified": article.dateModified || article.datePublished,
  "author": {
    "@type": "Organization",
    "name": PRODUCT_BRAND
  },
  "publisher": {
    "@type": "Organization",
    "name": PRODUCT_BRAND,
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_ORIGINS.pt}/logo.png`
    }
  },
  "image": article.image || `${SITE_ORIGINS.pt}/default-article-image.jpg`
});

export const createBreadcrumbSchema = (breadcrumbs: Array<{
  name: string;
  url: string;
}>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": crumb.name,
    "item": crumb.url
  }))
});

type BreadcrumbListSchema = {
  "@type"?: string;
  itemListElement?: Array<{ item?: string }>;
};

type ArticleSchema = {
  "@type"?: string;
  url?: string;
  mainEntityOfPage?: string | { "@id"?: string };
};

/** Last BreadcrumbList item and Article.url must match the page canonical URL (Search Console / rich results). */
export const alignStructuredDataBreadcrumbs = (
  structuredData: object | object[] | undefined,
  canonicalPageUrl: string,
): object | object[] | undefined => {
  if (!structuredData) return structuredData;

  const patchSchema = (schema: object): object => {
    const typed = schema as BreadcrumbListSchema & ArticleSchema;

    if (typed["@type"] === "Article") {
      const mainEntity =
        typeof typed.mainEntityOfPage === "object" && typed.mainEntityOfPage !== null
          ? { ...typed.mainEntityOfPage, "@id": canonicalPageUrl }
          : canonicalPageUrl;
      return {
        ...schema,
        url: canonicalPageUrl,
        mainEntityOfPage: mainEntity,
      };
    }

    if (typed["@type"] !== "BreadcrumbList") return schema;
    const elements = typed.itemListElement;
    if (!elements?.length) return schema;
    const lastIndex = elements.length - 1;
    return {
      ...schema,
      itemListElement: elements.map((el, index) =>
        index === lastIndex ? { ...el, item: canonicalPageUrl } : el,
      ),
    };
  };

  return Array.isArray(structuredData)
    ? structuredData.map(patchSchema)
    : patchSchema(structuredData);
};

export const createFAQSchema = (faqs: Array<{
  question: string;
  answer: string;
}>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const createRealEstateSchema = (property: {
  name: string;
  description: string;
  price?: string;
  location: string;
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "RealEstateListing",
  "name": property.name,
  "description": property.description,
  "price": property.price,
  "location": {
    "@type": "Place",
    "name": property.location
  },
  "image": property.image
});

export const createVisaServiceSchema = (visa: {
  name: string;
  description: string;
  url: string;
  minIncome?: string;
  processingTime?: string;
}) => {
  const descriptionParts = [visa.description];
  if (visa.processingTime) {
    descriptionParts.push(`Processing time: ${visa.processingTime}.`);
  }

  return {
    "@context": "https://schema.org",
    "@type": "GovernmentService",
    "name": visa.name,
    "description": descriptionParts.join(" "),
    "url": visa.url,
    "serviceType": "Visa Application",
    "areaServed": {
      "@type": "Country",
      "name": "Portugal",
    },
    "provider": {
      "@type": "GovernmentOrganization",
      "name": "Portugal Immigration Services",
      "areaServed": {
        "@type": "Country",
        "name": "Portugal",
      },
    },
    "termsOfService": visa.url,
    ...(visa.minIncome
      ? {
          offers: {
            "@type": "Offer",
            description: `Minimum income requirement: ${visa.minIncome}`,
            eligibleRegion: {
              "@type": "Country",
              name: "Portugal",
            },
          },
        }
      : {}),
  };
};

export type VisaPillarSchemaInput = {
  lang: SiteLanguage;
  visa: {
    name: string;
    description: string;
    path: string;
    /** When canonical differs from served path (e.g. legacy → /destinations/portugal/…) */
    canonicalPath?: string;
    minIncome?: string;
    processingTime?: string;
  };
  breadcrumbs: Array<{ name: string; path: string }>;
  faqs?: Array<{ question: string; answer: string }>;
};

/** Organization + advisory Service + GovernmentService + breadcrumbs + optional FAQ for visa pillar pages. */
export const createVisaPillarStructuredData = ({
  lang,
  visa,
  breadcrumbs,
  faqs,
}: VisaPillarSchemaInput) => {
  const origin = getSiteOrigin(lang);
  const visaPath = visa.canonicalPath ?? visa.path;
  const visaUrl = `${origin}${visaPath}`;
  const advisoryName =
    lang === "en"
      ? `${visa.name} Advisory — ${PRODUCT_BRAND}`
      : `Consultoria ${visa.name} — ${PRODUCT_BRAND}`;

  const schemas: object[] = [
    createOrganizationSchema(lang),
    createServiceSchema({
      name: advisoryName,
      description: visa.description,
      url: visaUrl,
    }),
    createVisaServiceSchema({
      name: visa.name,
      description: visa.description,
      url: visaUrl,
      minIncome: visa.minIncome,
      processingTime: visa.processingTime,
    }),
    createBreadcrumbSchema(
      breadcrumbs.map((crumb, index) => ({
        name: crumb.name,
        url:
          index === breadcrumbs.length - 1
            ? visaUrl
            : `${origin}${crumb.path}`,
      })),
    ),
  ];

  if (faqs?.length) {
    schemas.push(createFAQSchema(faqs));
  }

  return schemas;
};

export const createGuideSchema = (guide: {
  name: string;
  description: string;
  url: string;
  datePublished?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": guide.name,
  "description": guide.description,
  "url": guide.url,
  "datePublished": guide.datePublished,
  "author": {
    "@type": "Organization",
    "name": PRODUCT_BRAND
  }
});

export const createLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": PRODUCT_BRAND,
  "description": "International residency and relocation consultancy",
  "url": SITE_ORIGINS.pt,
  "telephone": "+351-XXX-XXX-XXX",
  "email": CONTACT_EMAILS.info,
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "PT",
    "addressLocality": "Lisboa",
    "addressRegion": "Lisboa"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "38.7223",
    "longitude": "-9.1393"
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  ],
  "priceRange": "€€€"
  // aggregateRating removed - add back when real reviews/ratings are available
});

export const createVideoSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": video.name,
  "description": video.description,
  "thumbnailUrl": video.thumbnailUrl,
  "uploadDate": video.uploadDate,
  "duration": video.duration,
  "publisher": {
    "@type": "Organization",
    "name": PRODUCT_BRAND,
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_ORIGINS.pt}/logo.png`
    }
  }
});

export const createItemListSchema = (items: Array<{
  name: string;
  description: string;
  url: string;
}>) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Service",
      "name": item.name,
      "description": item.description,
      "url": item.url
    }
  }))
});

export const createCollectionPageSchema = (collection: {
  name: string;
  description: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": collection.name,
  "description": collection.description,
  "url": collection.url,
  "isPartOf": {
    "@type": "WebSite",
    "name": PRODUCT_BRAND,
  },
});

export const createLocalBusinessSchemaEnhanced = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_ORIGINS.pt}/#organization`,
  "name": PRODUCT_BRAND,
  "alternateName": "DENVORA",
  "description": "Plataforma de inteligência de residência internacional — Portugal, Espanha, Itália e Grécia.",
  "url": SITE_ORIGINS.pt,
  "sameAs": [SITE_ORIGINS.en],
  "logo": {
    "@type": "ImageObject",
    "url": `${SITE_ORIGINS.pt}/logo.png`,
    "width": 200,
    "height": 200
  },
  "image": `${SITE_ORIGINS.pt}/og-image.png`,
  "telephone": "+351-XXX-XXX-XXX",
  "email": CONTACT_EMAILS.info,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Lisboa",
    "addressLocality": "Lisboa",
    "addressRegion": "Lisboa",
    "postalCode": "1000",
    "addressCountry": "PT"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "38.7223",
    "longitude": "-9.1393"
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  ],
  "priceRange": "€€€",
  "currenciesAccepted": "EUR",
  "paymentAccepted": "Cash, Credit Card, Bank Transfer",
  "areaServed": EU_DESTINATION_AREA_SERVED,
  "serviceArea": EU_DESTINATION_AREA_SERVED,
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": `${PRODUCT_BRAND} Services`,
    "itemListElement": [
      {
        "@type": "OfferCatalog",
        "name": "Visa Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Golden Visa Portugal"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "D7 Visa Portugal"
            }
          }
        ]
      },
      {
        "@type": "OfferCatalog",
        "name": "Real Estate Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Buyer Agent Portugal"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Property Investment Consultancy"
            }
          }
        ]
      }
    ]
  },
  // aggregateRating removed - add back when real reviews/ratings are available
  // Social media links - commented out until social media channels are set up
  // "sameAs": [
  //   "https://www.facebook.com/goldportugal",
  //   "https://www.linkedin.com/company/goldportugal",
  //   "https://www.instagram.com/goldportugal"
  // ]
});